import { useEffect, useState } from 'react';

import { AdPartner } from '../../../Components/AdPartner';
import { Button, DatePicker, InputPhoneNumber, InputTextArea } from '../../../Components/ui';
import Spinner from '../../../Components/ui/Spinner';
import { formatToReadableDate } from '../../../Helpers/dateUtils';
import { addJornayaScript, createJornayaInput, JORNAYA_INPUT_ID } from '../../../Helpers/jornaya';
import postScheduleCallback from '../../../Services/postScheduleCallback';
import CallbackHeaderText from './CallbackHeaderText';
import {
    dateValidator,
    getBrowserTimezone,
    getIncludedTimes,
    getMaxDate,
    userClickedADateElement,
} from './CallbackHelpers';
import CallbackSummary from './CallbackSummary';
import DisclosureText from './DisclosureText';
const defaultCallbackData = {
    date: undefined,
    time: undefined,
    phoneNumber: undefined,
    notes: '',
    jornaya_id: undefined,
    timezone: undefined,
};

const CallbackComponent = ({ schedulerData = {}, pageAds = {} }) => {
    const [callbackFormData, setCallbackFormData] = useState(defaultCallbackData);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [showError, setShowError] = useState();
    const [showDateError, setShowDateError] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [timeIsSelected, setTimeIsSelected] = useState(false);
    const [summaryData, setSummaryData] = useState();
    const fullTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserTimezoneName = getBrowserTimezone(false);
    const [includeTimes, setIncludeTimes] = useState([]);

    // Recalculate includeTimes whenever selectedDate or schedulerData changes
    useEffect(() => {
        const times = getIncludedTimes(selectedDate, schedulerData);
        setIncludeTimes(times);
    }, [selectedDate, schedulerData.id]);

    // Set the jornaya ID when the component mounts
    useEffect(() => {
        // jornaya compliance script
        addJornayaScript();

        /* this element gets populated with the leadid token value, which we send to the create lead API */
        createJornayaInput('callback-form');

        setTimeout(() => {
            const leadIdToken = document.getElementById(JORNAYA_INPUT_ID)?.value;
            setCallbackFormData(prevData => ({
                ...prevData,
                jornaya_id: leadIdToken,
            }));
        }, 1000);
    }, []);

    // Set the initial date and time when the component mounts
    useEffect(() => {
        let date = new Date();
        setSelectedDate(date);

        if (schedulerData.callback_time && schedulerData.callback_time !== '') {
            date = new Date(schedulerData.callback_time);

            if (date.getDate() < currentDateTime.getDate()) {
                setSelectedDate(currentDateTime);
            } else {
                setSelectedDate(date);
            }
        }

        setCallbackFormData(prev => ({
            ...prev,
            date: date,
            time: schedulerData.callback_time,
            phoneNumber: schedulerData.phone_number || '',
            notes: schedulerData.comment || '',
        }));
    }, []);

    // handle the phone number, date or time change
    const handleChange = (type, dateTime, event) => {
        switch (type) {
            case 'date':
                if (userClickedADateElement(event)) {
                    dateTime.setHours(23, 59, 59, 999);
                    setSelectedDate(dateTime);
                    setTimeIsSelected(false);
                    setCallbackFormData({
                        ...callbackFormData,
                        date: dateTime,
                        time: '00:00',
                    });
                } else {
                    setSelectedDate(dateTime);
                    setTimeIsSelected(true);
                    setShowDateError(false);
                    setCallbackFormData({
                        ...callbackFormData,
                        date: dateTime,
                        time: dateTime.toLocaleTimeString('en-US', {
                            hour12: false,
                            hour: '2-digit',
                            minute: '2-digit',
                        }),
                    });
                }
                break;

            case 'phoneNumber':
                setShowError(false);
                setCallbackFormData({
                    ...callbackFormData,
                    phoneNumber: dateTime.target.value,
                });
                break;

            case 'notes':
                setCallbackFormData({
                    ...callbackFormData,
                    notes: dateTime.target.value,
                });
                break;

            default:
                console.warn(`Unhandled form field type: ${type}`);
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        let hasErrors = false;

        // Check if time is selected
        if (!timeIsSelected) {
            setShowDateError(true);
            hasErrors = true;
        } else {
            setShowDateError(false); // Clear the error if time is selected
        }

        // Check if phone number is valid
        const isPhoneNumberValid = callbackFormData.phoneNumber?.replace(/\D/g, '').length === 10;
        if (!isPhoneNumberValid) {
            setShowError(true);
            hasErrors = true;
        } else {
            setShowError(false); // Clear the error if phone number is valid
        }

        // If there are any errors, stop the submission
        if (hasErrors) {
            return;
        }

        setIsLoading(true);

        const callbackDay = String(callbackFormData.date.getDate()).padStart(2, '0');
        const callbackMonth = String(callbackFormData.date.getMonth() + 1).padStart(2, '0');
        const callbackYear = String(callbackFormData.date.getFullYear()).padStart(4, '0');
        const hashKey = schedulerData.id;
        const submitData = {
            date: callbackFormData.date?.toDateString(),
            time: callbackFormData.time,
            timezone: fullTimezone,
            phone_number: callbackFormData.phoneNumber.replace(/\D/g, ''), // Remove non-digits
            comment: callbackFormData.notes || '',
            jornaya_id: callbackFormData.jornaya_id,
            callback_at: `${callbackYear}-${callbackMonth}-${callbackDay} ${callbackFormData.time}`,
        };

        // Make the API call
        try {
            await postScheduleCallback(hashKey, submitData);
            setIsSuccess(true);
            setIsLoading(false);

            const summary = {
                customerName: schedulerData.contact_name,
                dateTime: formatToReadableDate(callbackFormData.date, fullTimezone),
                comment: callbackFormData.notes,
                phoneNumber: callbackFormData.phoneNumber,
            };
            setSummaryData(summary);
        } catch (error) {
            console.error('Failed to post: ', error);
            setIsSuccess(false);
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setCallbackFormData(defaultCallbackData);
        setSelectedDate(null);
        setTimeIsSelected(false);
        setShowDateError(false);
        setShowError(false);
    };

    const currentDateTime = new Date();

    return (
        <div className="callback-container">
            {isLoading ? (
                <Spinner show={true} fullScreen={true} />
            ) : isSuccess ? (
                <CallbackSummary data={summaryData} />
            ) : (
                <>
                    <CallbackHeaderText schedulerData={schedulerData} />

                    <form onSubmit={handleSubmit}>
                        <DatePicker
                            id="datePicker"
                            name="datePicker"
                            allowSameDay={true}
                            selected={selectedDate}
                            maxDate={getMaxDate(currentDateTime, schedulerData)}
                            minTime={new Date(new Date().setHours(0, 0, 0))}
                            maxTime={new Date(new Date().setHours(23, 59, 59))}
                            excludeDates={[]}
                            includeTimes={includeTimes}
                            filterDate={date => dateValidator(date, false, schedulerData)}
                            showTimeSelect
                            dateFormat="MMMM d, yyyy h:mm aa"
                            onChange={(date, event = {}) => handleChange('date', date, event)}
                            timeCaption={'Time (' + browserTimezoneName + ')'}
                            timeInputLabel="Set Time:"
                            hasError={showDateError}
                            showWarningIcon
                            errorText="Callback date and time are required"
                        />
                        <InputPhoneNumber
                            label="Confirm Phone"
                            isRequired
                            id="phoneNumber"
                            name="phoneNumber"
                            value={callbackFormData.phoneNumber || ''}
                            onChange={phoneNumber => handleChange('phoneNumber', phoneNumber)}
                            pattern="[0-9]{10}"
                            placeholder="555-555-5555"
                            inlineStyle={{
                                width: '170px',
                            }}
                            showError={showError}
                            showWarningIcon
                        />

                        <InputTextArea
                            id="notes"
                            name="notes"
                            label="Optional Comments"
                            value={callbackFormData.notes}
                            onChange={notes => handleChange('notes', notes)}
                            rows="4"
                            placeholder="Add comments"
                        />

                        <div className="callback-disclosure">
                            <DisclosureText submitButtonText="Submit your time" callbackDisclosure={true} />
                        </div>
                        <div className="button-group">
                            <Button type="submit" btn-type="primary">
                                Submit your time
                            </Button>
                            <Button type="button" btn-type="secondary" onClick={handleClear}>
                                Clear
                            </Button>
                        </div>
                    </form>
                </>
            )}
            {pageAds.length > 0 && (
                <div className="ad-container">
                    <p className="disclosure-label">Important Advertising Disclosure Below</p>
                    <div className="paid-partners">
                        {pageAds.map((ad, index) => (
                            <AdPartner key={ad.id} adIndex={index} partnerData={ad} />
                        ))}
                    </div>
                    <div className="disclosure-container">
                        <label className="disclosure-text">
                            <strong>Disclaimer:</strong> The advertisers appearing on this website are providers of
                            insurance products and services from which Tivly receives compensation (“Sponsors”).
                            Compensation may impact where the Sponsors appear on this website (including the order in
                            which they appear). Our websites do not, and are not intended to, provide a comprehensive
                            list of all companies that may provide the products and services you are seeking.
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallbackComponent;
